/**
 * Today Page constants for colors, thresholds, and configuration.
 */

// ============ Color Palette ============

export const COLORS = {
  // Conduct Score distribution
  CONDUCT_GOOD: '#22C55E', // Green - scores 7-10
  CONDUCT_MODERATE: '#F59E0B', // Amber - scores 4-6
  CONDUCT_POOR: '#EF4444', // Red - scores 1-3

  // Dissatisfaction distribution
  DISSATISFIED: '#EF4444', // Red
  NEUTRAL: '#94A3B8', // Slate
  SATISFIED: '#22C55E', // Green

  // Volume distribution
  COMPLAINTS: '#EF4444', // Red
  FEEDBACK: '#3B82F6', // Blue
  ENQUIRIES: '#94A3B8', // Slate

  // Trend indicators
  TREND_UP_POSITIVE: '#22C55E', // Green (good)
  TREND_UP_NEGATIVE: '#EF4444', // Red (bad)
  TREND_DOWN_POSITIVE: '#22C55E',
  TREND_DOWN_NEGATIVE: '#EF4444',
  TREND_STABLE: '#94A3B8', // Slate

  // Card backgrounds
  CARD_BG: '#FFFFFF',
  CARD_BORDER: '#E2E8F0',

  // Donut chart
  DONUT_TRACK: '#F1F5F9', // Light slate for unused portion
} as const;

// ============ Thresholds ============
export const THRESHOLDS = {
  // Conduct Score (1-10 scale)
  CONDUCT_GOOD_MIN: 7,
  CONDUCT_MODERATE_MIN: 4,

  // Dissatisfaction thresholds for color coding
  DISSATISFACTION_CONCERNING: 40, // Above 40% = concerning
  DISSATISFACTION_CRITICAL: 60, // Above 60% = critical

  // Delta significance (for showing arrows)
  DELTA_SIGNIFICANT: 0.1, // Show trend if delta > 10%
} as const;

// ============ Donut Chart Configuration ============

export const DONUT_CONFIG = {
  SIZE: 120, // Diameter in pixels
  STROKE_WIDTH: 12, // Ring thickness
  INNER_RADIUS: 42, // Creates the "donut hole"
  OUTER_RADIUS: 54,
  CENTER_FONT_SIZE: 24,
  UNIT_FONT_SIZE: 12,
} as const;

// ============ Layout Configuration ============

export const LAYOUT = {
  PAGE_PADDING: 24,
  SECTION_GAP: 24,
  CARD_GAP: 16,
  CARD_PADDING: 20,
  CARD_BORDER_RADIUS: 12,
} as const;

// ============ API Configuration ============

export const API = {
  ENDPOINT: '/v1/today/metrics',
  STALE_TIME: 30000, // 30 seconds
  REFETCH_INTERVAL: 60000, // 1 minute
} as const;

// ============ Conduct Score Badge Colors ============

export const CONDUCT_BADGE_COLORS = {
  CRITICAL: {
    bg: '#FEE2E2', // Red-100
    text: '#DC2626', // Red-600
    border: '#FECACA', // Red-200
  },
  WARNING: {
    bg: '#FEF3C7', // Amber-100
    text: '#D97706', // Amber-600
    border: '#FDE68A', // Amber-200
  },
  GOOD: {
    bg: '#DCFCE7', // Green-100
    text: '#16A34A', // Green-600
    border: '#BBF7D0', // Green-200
  },
} as const;

// ============ Consumer Duty Configuration ============
// Per GO-57: Each card shows donut chart with dissatisfaction/harm/strain bars

export const CONSUMER_DUTY_CONFIG = {
  productsAndServices: {
    title: 'Products & Services',
    filterKey: 'products-services',
    indicatorLabels: {
      dissatisfaction: 'Dissatisfaction',
      harm: 'Harm',
      strain: 'Strain',
    },
  },
  priceAndValue: {
    title: 'Price & Value',
    filterKey: 'price-value',
    indicatorLabels: {
      dissatisfaction: 'Dissatisfaction',
      harm: 'Harm',
      strain: 'Strain',
    },
  },
  consumerUnderstanding: {
    title: 'Consumer Understanding',
    filterKey: 'understanding',
    indicatorLabels: {
      dissatisfaction: 'Dissatisfaction',
      harm: 'Harm',
      strain: 'Strain',
    },
  },
  consumerSupport: {
    title: 'Consumer Support',
    filterKey: 'support',
    indicatorLabels: {
      dissatisfaction: 'Dissatisfaction',
      harm: 'Harm',
      strain: 'Strain',
    },
  },
} as const;

// ============ Indicator Bar Configuration ============

export const INDICATOR_BAR = {
  HEIGHT: 8,
  BORDER_RADIUS: 4,
  TRACK_COLOR: '#E2E8F0',
  MIN_WIDTH_PERCENT: 5, // Minimum visible width
} as const;
